import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'i18n/useTranslation';

interface ConsentOptions {
  necessary: boolean
  analytics: boolean
  preferences: boolean
  marketing: boolean
}

declare global {
  interface Window {
    gtag: (command: string, action: string, params: object) => void
  }
}

interface CookiesProps {
  showBanner: boolean;
  setShowBanner: Dispatch<SetStateAction<boolean>>;
}

export const Cookies: React.FC<CookiesProps> = ({ showBanner, setShowBanner }) => {
  const { cookiesContent } = useTranslation();

  function hideBanner() {
    setShowBanner(false);
  }

  function setConsent(consent: ConsentOptions) {
    const consentMode = {
      functionality_storage: consent.necessary ? 'granted' : 'denied',
      security_storage: consent.necessary ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      personalization: consent.preferences ? 'granted' : 'denied',
    };

    // Only call gtag if it exists
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', consentMode);
    }

    localStorage.setItem('consentMode', JSON.stringify(consentMode));
  }

  const handleAcceptAll = () => {
    setConsent({
      necessary: true,
      analytics: true,
      preferences: true,
      marketing: true,
    });
    hideBanner();
  };

  const handleDeclineAll = () => {
    setConsent({
      necessary: false,
      analytics: false,
      preferences: false,
      marketing: false,
    });
    hideBanner();
  };

  useEffect(() => {
    if (localStorage.getItem('consentMode') !== null) {
      setShowBanner(false);
    }
  }, [setShowBanner]);

  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed left-0 top-0 z-[10000] flex w-full items-center justify-center backdrop-blur-md bg-checkout-card-gradient">
      <div className="relative w-full p-6 shadow-2xl">
        <div className="mb-6 text-center">
          <h3 className="mb-4 font-poppins text-[22.5px] font-bold text-[#ECEAED]">
            {cookiesContent.cookiesTitle}
          </h3>
        </div>
        <div className="mb-8 font-objektiv-mk3 text-[12.5px] md:text-[15px]">
          <p className="text-center font-poppins leading-relaxed text-[#ECEAED]">
            {cookiesContent.cookiesDescriptionOne}
          </p>
          <p className="text-center leading-relaxed text-[#ECEAED]">
            {cookiesContent.cookiesDescriptionTwo}
          </p>
          <p className="text-center leading-relaxed text-[#ECEAED]">
            {cookiesContent.cookiesDescriptionThree}
          </p>
        </div>
        <div className="flex flex-row justify-center gap-3 sm:gap-4">
          <button
            type="button"
            id="btn-accept-all"
            onClick={handleAcceptAll}
            className="max-w-[275px] flex-1 rounded-lg bg-[#8233CC] px-4 py-2 font-objektiv-mk3 text-[12.5px] font-bold text-[#ECEAED] transition-colors duration-200 hover:bg-[#7C3AED] md:text-[17.5px]"
          >
            {cookiesContent.cookiesAcceptBtnText}
          </button>
          <button
            type="button"
            id="btn-decline-all"
            onClick={handleDeclineAll}
            className="max-w-[275px] flex-1 rounded-lg bg-[#8233CC] px-4 py-2 font-objektiv-mk3 text-[12.5px] font-bold text-[#ECEAED] transition-colors duration-200 hover:bg-[#7C3AED] md:text-[17.5px]"
          >
            {cookiesContent.cookiesDeclineBtnText}
          </button>
        </div>
      </div>
    </div>
  );
};
